import React from 'react';
import { Chip, makeStyles, useTheme } from '@material-ui/core';
import { CatalogTable, CatalogTableColumnsFunc } from '@backstage/plugin-catalog';
import { LifecycleColor, lifecycleColors, radius } from '../../theme/tokens';

const useStyles = makeStyles({
  badge: {
    height: 22,
    fontSize: '0.72rem',
    fontWeight: 600,
    letterSpacing: '0.01em',
    borderRadius: radius.sm,
    textTransform: 'capitalize',
  },
  tagChip: {
    margin: '0 4px 2px 0',
  },
});

const resolveLifecycleColor = (
  lifecycle: string,
  mode: 'light' | 'dark',
): LifecycleColor => {
  const colors = lifecycleColors[mode];
  return colors[lifecycle.toLowerCase()] ?? colors.default;
};

const LifecycleBadge = ({ lifecycle }: { lifecycle: string }) => {
  const classes = useStyles();
  const theme = useTheme();
  const mode = theme.palette.type === 'dark' ? 'dark' : 'light';
  const color = resolveLifecycleColor(lifecycle, mode);

  return (
    <Chip
      className={classes.badge}
      size="small"
      variant="outlined"
      label={lifecycle}
      style={{
        backgroundColor: color.background,
        color: color.color,
        borderColor: color.border,
      }}
    />
  );
};

const TagBadges = ({ tags }: { tags: string[] }) => {
  const classes = useStyles();

  return (
    <>
      {tags.map(tag => (
        <Chip
          key={tag}
          className={`${classes.badge} ${classes.tagChip}`}
          size="small"
          label={tag}
        />
      ))}
    </>
  );
};

/**
 * Default catalog columns with the Lifecycle and Tags cells re-rendered as
 * modern badges. Lifecycle values get distinct semantic colors
 * (production=green, experimental=violet, deprecated=red, ...).
 */
export const catalogColumnsFunc: CatalogTableColumnsFunc = entityListContext =>
  CatalogTable.defaultColumnsFunc(entityListContext).map(column => {
    // Column titles are translated React elements in this Backstage version,
    // so match on the stable data-path `field` instead.
    if (column.field === 'entity.spec.lifecycle') {
      return {
        ...column,
        render: ({ entity }) => {
          const lifecycle = entity.spec?.lifecycle;
          return typeof lifecycle === 'string' && lifecycle ? (
            <LifecycleBadge lifecycle={lifecycle} />
          ) : null;
        },
      };
    }
    if (column.field === 'entity.metadata.tags') {
      return {
        ...column,
        render: ({ entity }) => (
          <TagBadges tags={entity.metadata.tags ?? []} />
        ),
      };
    }
    return column;
  });
